import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import ShopOrderModelMC from '../../../models/ShopOrderModelMC';
import { SIDEBAR_ACTIONS } from '../../../../constants/actionsConstants';
import { DELIVERY_STATUSES_WITH_ATTACHMENT } from '../constants/deliveryStatus';

const DELIVERY_PROOF_MODAL_KEY = 'DeliveryProofModal';

/**
 * Hook to update an order's delivery status (with optional proof attachment).
 * @param {Object} order
 * @param {Function} updateOrderInList - callback(updatedOrder)
 */
const useOrderDeliveryStatus = (order, updateOrderInList) => {
  const dispatch = useDispatch();
  const [newStatus, setNewStatus] = useState('');
  const [loading, setLoading] = useState(false);

  const closeDeliveryProofModal = useCallback(() => {
    dispatch({
      type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR,
      payload: { componentKey: DELIVERY_PROOF_MODAL_KEY }
    });
  }, [dispatch]);

  const updateDeliveryStatus = useCallback(async (status, attachment = null) => {
    if (!order?.web_id) return null;

    setLoading(true);

    try {
      let response;

      if (attachment) {
        const formData = new FormData();
        formData.append('id', order.web_id);
        formData.append('order[delivery_status]', status);
        formData.append('order[delivery_attachment]', attachment);
        response = await ShopOrderModelMC.save(formData);
      } else {
        const orderPayload = { delivery_status: status };
        const shouldRemoveAttachment = !DELIVERY_STATUSES_WITH_ATTACHMENT.includes(status)
          && (order.delivery_attachment?.url || order.delivery_attachment);

        if (shouldRemoveAttachment) {
          orderPayload.remove_delivery_attachment = true;
        }

        response = await ShopOrderModelMC.save({
          id: order.web_id,
          order: orderPayload
        });
      }

      const updatedOrder = response?.order || response;
      if (updateOrderInList && updatedOrder) {
        updateOrderInList(updatedOrder);
      }

      return updatedOrder;
    } finally {
      setLoading(false);
    }
  }, [order, updateOrderInList]);

  const handleSaveDeliveryProof = useCallback(async (attachment, status) => {
    const targetStatus = status || newStatus;
    await updateDeliveryStatus(targetStatus, attachment);
    closeDeliveryProofModal();
  }, [newStatus, updateDeliveryStatus, closeDeliveryProofModal]);

  const handleStatusChange = useCallback(async (status) => {
    setNewStatus(status);

    if (DELIVERY_STATUSES_WITH_ATTACHMENT.includes(status)) {
      dispatch({
        type: SIDEBAR_ACTIONS.OPEN_SIDEBAR,
        payload: {
          componentKey: DELIVERY_PROOF_MODAL_KEY,
          props: {
            onSave: (attachment) => handleSaveDeliveryProof(attachment, status)
          }
        }
      });
    } else {
      await updateDeliveryStatus(status);
    }
  }, [dispatch, handleSaveDeliveryProof, updateDeliveryStatus]);

  return {
    loading,
    newStatus,
    handleStatusChange,
    handleSaveDeliveryProof,
    updateDeliveryStatus
  };
};

export default useOrderDeliveryStatus;
